import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Label } from "./label";

export interface StringSelectOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface StringSelectProps<T extends string> {
  value?: T;
  onValueChange: (value: T) => void;
  options: readonly StringSelectOption<T>[];
  /** Optional label rendered above the select */
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  valueClassName?: string;
}

/**
 * A type-safe select component for string literal unions.
 *
 * Unlike the base Select component which types onValueChange as (value: string) => void,
 * this component preserves the literal type through the generic parameter.
 *
 * This component also handles boilerplate like label display and item rendering
 *
 * @example
 * <StringSelect
 *   label="Size"
 *   value={size}
 *   onValueChange={setSize}
 *   options={[
 *     { value: "sm", label: "Small" },
 *     { value: "md", label: "Medium" },
 *     { value: "lg", label: "Large" },
 *   ]}
 * />
 */
export function StringSelect<T extends string>({
  value,
  onValueChange,
  options,
  label,
  placeholder,
  className,
  disabled,
  valueClassName,
}: StringSelectProps<T>) {
  const select = (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue className={valueClassName} placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (label) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {select}
      </div>
    );
  }

  return select;
}
